import { Item, type Game, type Tile } from "..";
import type { Drop, ItemAnimation, ItemData, ItemState, Position, instanceId, itemId } from "../../data/types";
import { System } from "../../lib/core";

export type ItemEvents = {};

/** Multiply each entry in `drops` by `count`. Returned object is a fresh shallow copy. */
function scaleDrops(drops: Drop, count: number): Drop {
  const scaled: Drop = {};
  for (const k in drops) (scaled as Record<string, number>)[k] = drops[k as keyof Drop]! * count;
  return scaled;
}

type TapRecipe = ItemData["tap"]["recipes"][number];
type ChargeRecipe = ItemData["charge"]["recipes"][number];

export type TapPreview = {
  item: Item;
  recipe: TapRecipe;
  spawn: { itemId: itemId; tile: Tile }[];
  /** Total drops the recipe asks for; spawn may be shorter if the board is full (apply is strict). */
  requested: number;
  /**
   * Chain multiplier — for `chain: true` recipes, the effective drop count
   * fired this tap (charge cost = chainCount × recipe.charge). 1 for
   * non-chain recipes.
   */
  chainCount: number;
};

export type AutoSpawnPreview = {
  item: Item;
  recipe: ChargeRecipe;
  spawn: { itemId: itemId; tile: Tile }[];
  /** Total drops the recipe asks for; spawn may be shorter if the board is full (apply is strict). */
  requested: number;
};

type SpawnItemOverrides = {
  position?: Position;
  charge?: ItemState["charge"];
  exhaustIndex?: ItemState["exhaustIndex"];
};

export class ItemSystem extends System<Game>()<ItemEvents>([]) {
  spawn(
    {
      itemId,
      tile,
      animation,
    }: {
      itemId: itemId;
      tile: Tile | null; // can spawn floating items
      animation: Omit<ItemAnimation, "to" | "startTick" | "endTick"> | null;
    },
    item?: SpawnItemOverrides,
  ): Item {
    const game = this.game;
    const data = game.data.items[itemId];
    if (!data) {
      throw new Error(`Item data not found for id: ${itemId}`);
    }

    const uid: instanceId = `${itemId}#${++game._lastUid}`;
    const position = tile?.position ?? item?.position;
    if (!position) {
      throw new Error(`Item has no position`);
    }

    const instance = new Item({
      id: itemId,
      uid,
      data,
      position,
      charge: data.charge ? 0 : undefined,
      ...item,
    });
    if (tile) {
      if (tile.item) {
        throw new Error(`Tile already has an item: ${tile.key}`);
      }
      tile.item = instance;
      instance.position = tile.position;
    }
    if (animation) {
      if (!instance.position) {
        throw new Error(`Item has no position`);
      }
      instance.animation = {
        ...animation,
        startTick: game.state.tick,
        endTick: game.state.tick + game.data.animations[animation.type].duration,
        to: instance.position,
      };
    }

    game.state.items.set(uid, instance);
    return instance;
  }

  /**
   * Compute the spawn assignment a tap on `item` would produce.
   *
   * Walks tap recipes from highest charge to lowest and picks the highest one
   * that (a) the current charge satisfies and (b) fully fits the board. For
   * `chain: true` recipes the effective drop count is `min(floor(charge/cost),
   * cells fitting)`, falling through to lower counts until one fits. Preview
   * and apply use the same recipe — what the highlight shows is what tap will
   * drop. Returns null when nothing fires.
   */
  previewTapSpawn(item: Item): TapPreview | null {
    if (!item.position) return null;
    const recipes = item.data.tap?.recipes;
    if (!recipes) return null;
    const itemTile = this.game.grid.tryAt(item.position);
    if (!itemTile) return null;

    for (let i = recipes.length - 1; i >= 0; i--) {
      const recipe = recipes[i]!;
      if (recipe.charge !== null && (item.charge === undefined || item.charge < recipe.charge)) continue;

      if (recipe.chain) {
        // Cost is required for chain: chargeUnit per chain step.
        if (recipe.charge === null || item.charge === undefined) continue;
        const maxAffordable = Math.floor(item.charge / recipe.charge);
        for (let count = maxAffordable; count >= 1; count--) {
          const expanded = scaleDrops(recipe.drops, count);
          const assignment = this.game.spawn.assign({
            from: itemTile,
            drops: expanded,
            range: recipe.dropRange,
          });
          if (assignment.fits) {
            return {
              item,
              recipe,
              spawn: assignment.spawn,
              requested: assignment.requested,
              chainCount: count,
            };
          }
        }
        continue;
      }

      const assignment = this.game.spawn.assign({
        from: itemTile,
        drops: recipe.drops,
        range: recipe.dropRange,
      });
      if (!assignment.fits) continue; // recipe doesn't fit; try the next-lower one
      return {
        item,
        recipe,
        spawn: assignment.spawn,
        requested: assignment.requested,
        chainCount: 1,
      };
    }
    return null;
  }

  /**
   * Apply a tap-spawn preview. Strict: only fires when all requested drops fit the board.
   * Empty-drop recipes (e.g. treasure chest tap) deduct charge without spawning.
   * Returns whether items were actually spawned (caller uses this to decide whether to settle).
   */
  applyTapSpawn(preview: TapPreview): boolean {
    const { item, recipe, spawn, requested, chainCount } = preview;
    if (spawn.length < requested) return false; // strict: skip if board can't fit all drops
    if (!item.position) return false;
    if (spawn.length > 0) this.spawnDrops(item.position, spawn);
    if (recipe.charge !== null && item.charge !== undefined) {
      item.charge -= recipe.charge * chainCount;
    }
    return spawn.length > 0;
  }

  /**
   * Compute the auto-spawn assignment a tick on `item` would produce.
   *
   * Walks charge recipes from highest charge to lowest, picks the highest
   * matching recipe that fully fits. Returns null when nothing fires.
   */
  previewAutoSpawn(item: Item): AutoSpawnPreview | null {
    if (!item.position) return null;
    if (item.charge === undefined) return null;
    const recipes = item.data.charge?.recipes;
    if (!recipes) return null;
    const itemTile = this.game.grid.tryAt(item.position);
    if (!itemTile) return null;

    const max = item.data.charge!.max;
    for (let i = recipes.length - 1; i >= 0; i--) {
      const recipe = recipes[i]!;
      if (item.charge < recipe.charge) continue;
      if (recipe.requiresMaxCharge && item.charge < max) continue;
      const assignment = this.game.spawn.assign({
        from: itemTile,
        drops: recipe.drops,
        range: recipe.dropRange,
      });
      if (!assignment.fits) continue;
      return { item, recipe, spawn: assignment.spawn, requested: assignment.requested };
    }
    return null;
  }

  /**
   * Apply an auto-spawn preview. Strict: only fires when all requested drops fit the board.
   * Returns whether anything was spawned.
   */
  applyAutoSpawn(preview: AutoSpawnPreview): boolean {
    const { item, recipe, spawn, requested } = preview;
    if (spawn.length < requested) return false;
    if (!item.position) return false;
    this.spawnDrops(item.position, spawn);
    if (item.charge !== undefined) item.charge -= recipe.charge;
    return true;
  }

  /**
   * Per-tick charge step: skip-if-no-charge, skip-if-animating, pausedInShade gate, init charge,
   * try auto-spawn (highest matching kind="spawn" recipe), increment charge if didn't spawn.
   * Returns whether the item produced a spawn this tick.
   */
  tickCharge(item: Item): boolean {
    if (!item.data.charge) return false;
    if (item.animation) return false;
    if (item.data.shade?.pausedInShade) {
      const tile = item.position ? this.game.grid.tryAt(item.position) : null;
      if (tile && tile.shade > 0) return false;
    }
    if (item.charge === undefined) item.charge = 0;

    const preview = this.previewAutoSpawn(item);
    if (preview && this.applyAutoSpawn(preview)) return true;

    if (item.charge < item.data.charge.max) item.charge++;
    return false;
  }

  spawnDrops(
    center: Position,
    spawn: { itemId: itemId; tile: Tile }[],
  ): void {
    const game = this.game;
    const dropDuration = game.data.animations["item:spawn"].duration;
    for (const [index, { itemId, tile }] of spawn.entries()) {
      const item = this.spawn({
        itemId,
        tile,
        animation: { type: "item:spawn", from: center },
      });
      if (item.animation) {
        const delay = dropDuration * index;
        item.animation.startTick += delay;
        item.animation.endTick += delay;
      }
    }
  }
}
